import { useState, useEffect } from 'react';
import { cn } from '@/lib/utils';
import { Shield, ShieldCheck, ShieldAlert, Link2, Hash, Loader2 } from 'lucide-react';

interface IntegrityIndicatorProps {
  isValid: boolean;
  recordsChecked: number;
  isLoading?: boolean;
  onVerify?: () => void;
}

export function IntegrityIndicator({
  isValid,
  recordsChecked,
  isLoading = false,
  onVerify,
}: IntegrityIndicatorProps) {
  const [pulseAnimation, setPulseAnimation] = useState(false);

  useEffect(() => {
    if (isValid) {
      setPulseAnimation(true);
      const timer = setTimeout(() => setPulseAnimation(false), 2000);
      return () => clearTimeout(timer);
    }
  }, [isValid]);

  return (
    <div
      className={cn(
        "relative p-6 rounded-xl border-2 transition-all duration-500 cursor-pointer",
        isValid
          ? "border-green-500 bg-gradient-to-br from-green-50 to-emerald-50"
          : "border-red-500 bg-gradient-to-br from-red-50 to-rose-50",
        isLoading && "opacity-70"
      )}
      onClick={onVerify}
    >
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden rounded-xl">
        <div
          className={cn(
            "absolute -top-10 -right-10 w-40 h-40 rounded-full opacity-10",
            isValid ? "bg-green-500" : "bg-red-500"
          )}
        />
        <div
          className={cn(
            "absolute -bottom-10 -left-10 w-32 h-32 rounded-full opacity-10",
            isValid ? "bg-emerald-500" : "bg-rose-500"
          )}
        />
      </div>

      <div className="relative flex items-center gap-6">
        {/* Icon with animation */}
        <div className="relative">
          <div
            className={cn(
              "absolute inset-0 rounded-full",
              pulseAnimation && "animate-ping",
              isValid ? "bg-green-400" : "bg-red-400",
              "opacity-30"
            )}
          />
          <div
            className={cn(
              "relative w-20 h-20 rounded-full flex items-center justify-center",
              isValid
                ? "bg-gradient-to-br from-green-400 to-emerald-500"
                : "bg-gradient-to-br from-red-400 to-rose-500"
            )}
          >
            {isLoading ? (
              <Loader2 className="w-10 h-10 text-white animate-spin" />
            ) : isValid ? (
              <ShieldCheck className="w-10 h-10 text-white" />
            ) : (
              <ShieldAlert className="w-10 h-10 text-white" />
            )}
          </div>
        </div>

        {/* Status text */}
        <div className="flex-1">
          <h3
            className={cn(
              "text-2xl font-bold",
              isValid ? "text-green-700" : "text-red-700"
            )}
          >
            {isLoading
              ? "Verificando..."
              : isValid
              ? "Integridad Verificada"
              : "Error de Integridad"}
          </h3>
          <p className={cn("text-sm mt-1", isValid ? "text-green-600" : "text-red-600")}>
            {isLoading
              ? "Analizando cadena de hashes..."
              : isValid
              ? "Todos los registros son autenticos e inmutables"
              : "Se detectaron inconsistencias en la cadena"}
          </p>

          {/* Stats */}
          <div className="flex items-center gap-6 mt-4">
            <div className="flex items-center gap-2">
              <Hash className={cn("w-4 h-4", isValid ? "text-green-500" : "text-red-500")} />
              <span className="text-sm font-medium">
                {recordsChecked.toLocaleString()} registros verificados
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Link2 className={cn("w-4 h-4", isValid ? "text-green-500" : "text-red-500")} />
              <span className="text-sm font-medium">
                Cadena SHA-256 {isValid ? "intacta" : "comprometida"}
              </span>
            </div>
          </div>
        </div>

        {/* Blockchain visualization */}
        <div className="hidden lg:flex items-center gap-1">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="flex items-center">
              <div
                className={cn(
                  "w-8 h-8 rounded-md flex items-center justify-center text-xs font-mono",
                  isValid
                    ? "bg-green-200 text-green-700"
                    : i === 3
                    ? "bg-red-300 text-red-700"
                    : "bg-green-200 text-green-700"
                )}
              >
                #{i + 1}
              </div>
              {i < 4 && (
                <div
                  className={cn(
                    "w-4 h-0.5",
                    isValid
                      ? "bg-green-400"
                      : i >= 2
                      ? "bg-red-400"
                      : "bg-green-400"
                  )}
                />
              )}
            </div>
          ))}
          <span className="text-xs text-muted-foreground ml-2">...</span>
        </div>
      </div>

      {/* Click hint */}
      <p className="absolute bottom-2 right-4 text-xs text-muted-foreground">
        Clic para verificar
      </p>
    </div>
  );
}
